import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import UploadFile from "components/_refactor/dropzone";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  CAMPAIGN_CATEGORIES,
  CONTENT_STATUS,
  MOCK_CONTENTS,
} from "../constants";

const EMPTY_FORM = {
  nome: "",
  url: "",
  categoria: null,
  campanha: null,
  playlist: "",
  descricao: "",
  duracao: "",
  status: null,
};

const campanhaOptions = MOCK_CONTENTS.filter((c) => c.tipo === "CAMPANHA").map(
  (c) => ({ value: c.id, label: c.titulo }),
);

export default function DialogVideo({ open, onClose, content }) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [videoFiles, setVideoFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (content) {
      setForm({
        nome: content.titulo || "",
        url: "",
        categoria:
          CAMPAIGN_CATEGORIES.find((c) => c.label === content.categoria) ||
          null,
        campanha: null,
        playlist: "",
        descricao: content.descricao || "",
        duracao: "12:34",
        status: CONTENT_STATUS.find((s) => s.value === content.status) || null,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, content]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    updateForm("url", url);
    if (url.includes("youtube") || url.includes("vimeo")) {
      updateForm("duracao", "— detectado automaticamente");
    }
  };

  const handleSave = () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome do vídeo é obrigatório.", {
        severity: "error",
      });
      return;
    }
    snackbar.showMessage(
      content ? "Vídeo atualizado com sucesso." : "Vídeo salvo com sucesso.",
      { severity: "success" },
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content ? "Editar Vídeo" : "Cadastro de Vídeo"}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome do vídeo"
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="URL do vídeo (YouTube / Vimeo)"
                  value={form.url}
                  onChange={handleUrlChange}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Ou faça upload do vídeo
                </Typography>
                <UploadFile
                  message="Arraste o vídeo ou clique para selecionar"
                  accept={{ "video/*": [".mp4", ".mov", ".avi"] }}
                  onChangeFiles={setVideoFiles}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={CAMPAIGN_CATEGORIES}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Campanha vinculada"
                  value={form.campanha}
                  onChange={(v) => updateForm("campanha", v)}
                  options={campanhaOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Playlist"
                  value={form.playlist}
                  onChange={(e) => updateForm("playlist", e.target.value)}
                  placeholder="Nome da playlist"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail personalizada
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={{ "image/*": [".jpg", ".jpeg", ".png"] }}
                  onChangeFiles={setThumbnailFiles}
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Duração"
                  value={form.duracao}
                  disabled
                  placeholder="Detectada automaticamente"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Salvar vídeo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
